import React, { useState, useContext } from 'react';
import { StoryForm } from "./StoryForm/StoryForm";
import { Sparkles, BookOpen, Mic, Image, Shield } from "lucide-react";
import image8 from '../assets/images/8.jpg';
import image9 from '../assets/images/9.jpg';
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from './UserContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from "../api/apiClient";

const features = [
  {
    icon: <BookOpen className="w-8 h-8 text-lavenderPurple" />,
    title: "Personliga berättelser",
    description: "Varje bok är unik, baserad på barnets idéer och önskemål.",
  },
  {
    icon: <Mic className="w-8 h-8 text-turquoise" />,
    title: "AI-berättarröst",
    description: "Professionell röstsyntes skapar en levande och engagerande berättelse.",
  },
  {
    icon: <Image className="w-8 h-8 text-softYellow" />,
    title: "Magiska illustrationer",
    description: "Vackra bilder som passar perfekt till varje saga.",
  },
  {
    icon: <Shield className="w-8 h-8 text-lightCoralPink" />,
    title: "Trygghet & Kvalitet",
    description: "Säkert innehåll skapat med noggrant utvalda AI-modeller.",
  },
];

const BlaHome = () => {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { currentUser, isPrelaunch } = useContext(UserContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error('Du måste godkänna villkoren för att fortsätta', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await api.post('/api/prelaunch-signup', { email });
      setSubmitSuccess(true);
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
      const status = error.response?.status;
      if (status === 409) {
        toast.info('Denna e-postadress är redan registrerad', {
          position: 'top-center',
          autoClose: 3000,
        });
      } else {
        toast.error(error.response?.data?.error || 'Något gick fel', {
          position: 'top-center',
          autoClose: 3000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Klickhanterare för "Skapa din första bok"
  const handleCreateClick = () => {
    if (!currentUser) {
      toast.info('Du måste vara inloggad för att skapa en bok', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }
    navigate('/skapa');
  };

  if (showForm) {
    return <StoryForm />;
  }

  return (
    <div>
      {/* Huvudsektion */}
      <section className="py-20 bg-gradient-to-b from-warmWhite to-warmWhite">
        <div className="container mx-auto px-4 flex flex-col items-center">
          <h1 className="text-4xl md:text-5xl font-bold text-magic-900 mb-6 text-center">
            Skapa Magiska Berättelser för Ditt Barn
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 text-center">
            Föreställ dig glädjen i ditt barns ögon när de hör en berättelse där de är hjälten...
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="text-center bg-white rounded-xl border border-gray-100 shadow-md">
              <img src={image8} alt="Studier visar" className="w-full h-auto object-cover rounded-t-lg" />
              <div className="p-6">
                <p className="text-lg text-gray-600">
                  Studier visar att barn blir mer engagerade när de ser sig själva i en berättelse...
                </p>
              </div>
            </div>
            <div className="text-center bg-white rounded-xl border border-gray-100 shadow-md">
              <img src={image9} alt="Oavsett äventyr" className="w-full h-auto object-cover rounded-t-lg" />
              <div className="p-6">
                <p className="text-lg text-gray-600">
                  Oavsett om det handlar om riddare och drakar eller mysiga godnattsagor...
                </p>
              </div>
            </div>
          </div>

          {/* Prelaunch eller CTA */}
          <div className="container mx-auto px-4 mt-16 flex flex-col items-center">
            {isPrelaunch ? (
              <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                {submitSuccess ? (
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-grey-600 mb-2">Tack för din förregistrering!</h3>
                    <p className="text-gray-600">
                      Du har fått en bekräftelse med rabattkoder till din e-post.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-center mb-4 text-gray-800">
                      Få 20% rabatt vid lansering!
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="Din e-postadress"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise"
                      />
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="terms-bla"
                          checked={acceptTerms}
                          onChange={e => setAcceptTerms(e.target.checked)}
                          className="mt-1 mr-2"
                        />
                        <label htmlFor="terms-bla" className="text-sm text-gray-600">
                          Jag godkänner villkoren och att min e-post används för att skicka rabattkod.
                        </label>
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting || !acceptTerms}
                        className={`w-full bg-turquoise hover:bg-turquoise-dark text-white font-semibold py-3 px-6 rounded-full flex items-center justify-center transition-colors ${
                          (!acceptTerms || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        {isSubmitting ? 'Skickar...' : 'Få rabattkod'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={handleCreateClick}
                className="bg-turquoise hover:bg-turquoise-dark text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center w-4/5 sm:w-2/5"
              >
                <Sparkles className="w-6 h-6 mr-2" />
                Skapa din första bok!
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Toast-container */}
      <ToastContainer />
    </div>
  );
};

export default BlaHome;
